import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building, Pencil, Trash2, Plus } from "lucide-react";

const emptyForm = { name: "", code: "", description: "", timezone_offset: 7 };

const TZ_LABELS = { 7: "WIB (UTC+7)", 8: "WITA (UTC+8)", 9: "WIT (UTC+9)" };

export default function AdminUnits() {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/work-units");
      setUnits(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, code: u.code || "", description: u.description || "", timezone_offset: u.timezone_offset ?? 7 });
    setOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, timezone_offset: Number(form.timezone_offset) };
      if (editing) {
        await api.patch(`/admin/work-units/${editing.unit_id}`, payload);
        toast.success("Unit kerja diperbarui.");
      } else {
        await api.post("/admin/work-units", payload);
        toast.success("Unit kerja ditambahkan.");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal simpan.");
    }
  };
  const doDelete = async (u) => {
    if (!window.confirm(`Hapus unit kerja "${u.name}"?`)) return;
    try {
      await api.delete(`/admin/work-units/${u.unit_id}`);
      toast.success("Dihapus.");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal hapus.");
    }
  };

  return (
    <div data-testid={TESTIDS.adminUnitsPage} className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tiny">Manajemen Unit Kerja</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Unit Kerja</h1>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            Kelola divisi/bagian/unit kerja perusahaan. Setiap karyawan bisa ditugaskan ke satu unit kerja untuk memudahkan rekap laporan & pengaturan massal.
          </p>
        </div>
        <button data-testid={TESTIDS.createUnitBtn} onClick={openCreate}
          className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 hover:bg-black transition-colors text-sm font-semibold">
          <Plus className="w-4 h-4" /> Tambah Unit Kerja
        </button>
      </div>

      <div className="border border-black/10 bg-white">
        <table data-testid={TESTIDS.unitsTable} className="w-full text-sm">
          <thead className="bg-black text-white text-left">
            <tr><Th>Kode</Th><Th>Nama Unit Kerja</Th><Th>Zona Waktu</Th><Th>Deskripsi</Th><Th>Aksi</Th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-8 text-center text-gray-500">Memuat...</td></tr>}
            {!loading && units.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-gray-500">Belum ada unit kerja. Tambahkan minimal 1 untuk mulai assign ke karyawan.</td></tr>
            )}
            {!loading && units.map((u) => (
              <tr key={u.unit_id} className="border-t border-black/10 hover:bg-gray-50">
                <Td className="font-mono text-xs">{u.code || <span className="text-gray-400">—</span>}</Td>
                <Td className="font-semibold inline-flex items-center gap-2"><Building className="w-4 h-4 text-klein" />{u.name}</Td>
                <Td className="text-xs font-mono">{TZ_LABELS[u.timezone_offset ?? 7] || `UTC+${u.timezone_offset}`}</Td>
                <Td className="text-gray-600">{u.description || <span className="text-gray-400">—</span>}</Td>
                <Td>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(u)} className="border border-black/20 hover:bg-black hover:text-white p-1.5"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => doDelete(u)} className="border border-black/20 hover:bg-signal-late hover:text-white p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg border-black/20">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit Unit Kerja" : "Tambah Unit Kerja"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Nama Unit Kerja" required>
              <input data-testid={TESTIDS.unitFormName} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required className="input" placeholder="misal: Divisi Keuangan" />
            </Field>
            <Field label="Kode (opsional)">
              <input data-testid={TESTIDS.unitFormCode} value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                className="input font-mono" placeholder="misal: KEU" />
            </Field>
            <Field label="Zona Waktu">
              <select data-testid="unit-form-timezone"
                value={form.timezone_offset}
                onChange={(e) => setForm({ ...form, timezone_offset: e.target.value })}
                className="input">
                <option value={7}>WIB — UTC+7</option>
                <option value={8}>WITA — UTC+8</option>
                <option value={9}>WIT — UTC+9</option>
              </select>
              <div className="text-xs text-gray-500 mt-1">Waktu server (UTC) akan disesuaikan dengan zona ini saat menghitung Terlambat & Pulang Cepat.</div>
            </Field>
            <Field label="Deskripsi (opsional)">
              <textarea rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="input" />
            </Field>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="border border-black/20 px-4 py-2 hover:bg-gray-100 text-sm">Batal</button>
              <button data-testid={TESTIDS.unitFormSubmit} type="submit" className="bg-klein text-white px-5 py-2 hover:bg-black text-sm font-semibold">Simpan</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <style>{`.input { width:100%; border: 1px solid rgba(0,0,0,0.2); padding: 10px 12px; background: white; font-size: 14px; }`}</style>
    </div>
  );
}
function Field({ label, children, required }) {
  return <div><div className="label-tiny mb-2">{label}{required && <span className="text-signal-late"> *</span>}</div>{children}</div>;
}
const Th = ({ children }) => <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
